import React from 'react';
import { Calendar, ChevronRight } from 'lucide-react';
import { LibraryIcon, isLibraryIcon } from '../lib/iconLibrary';
import { getImageUrl } from '../utils/api';
import { socialMediaIcons } from '../lib/socialIcons';
import { SOCIAL_URL_KEYS } from '../lib/socialLinks';

// A deliberately faithful mock of the native app's home screen (taptile-ios/App.js).
// Values here mirror that file's StyleSheet, so what you see is what ships.
const NATIVE = {
  headerPadTop: 40,
  headerPadBottom: 20,
  headerImageHeight: 120,
  headerTitleSize: 24,
  welcomeSize: 14,
  sectionTitleSize: 18,
  tileWidthPct: 28,
  tileTitleSize: 11,
  newsTitleSize: 18,
  newsExcerptSize: 14,
  socialIconSize: 44,
  maxNewsItems: 3,   // the app requests ?limit=3
  tilesPerRow: 3,    // tile width is a fixed 28%
};

const contrastText = (hex = '#2563EB') => {
  const c = (hex || '').replace('#', '');
  if (c.length !== 6) return '#FFFFFF';
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#111827' : '#FFFFFF';
};

const borderRadiusFor = (shape) => {
  switch (shape) {
    case 'circle': return '50%';
    case 'rounded': return 20;
    case 'square': return 0;
    default: return 12;
  }
};

// Mirrors getTileVisuals() in taptile-ios/App.js
const tileVisuals = (tile, customer) => {
  const base = tile.color || customer?.tile_color || customer?.primary_color || '#2563EB';
  const override = tile.text_color && tile.text_color !== '#FFFFFF' ? tile.text_color : null;
  switch (customer?.tile_style || 'soft') {
    case 'solid':
    case 'gradient': // no gradient in the app — renders solid
      return { background: base, color: tile.text_color || customer?.tile_font_color || '#FFFFFF' };
    case 'outlined':
      return tile.color
        ? { background: base, border: `2px solid ${tile.border_color || base}`, color: contrastText(base) }
        : { background: '#FFFFFF', border: `2px solid ${tile.border_color || base}`, color: override || customer?.tile_font_color || base };
    default:
      return { background: /^#[0-9a-f]{6}$/i.test(base) ? `${base}15` : base, color: override || customer?.tile_font_color || base };
  }
};

const stripHtml = (html = '') => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

// Every way the app's layout deliberately differs from the web portal.
export const nativeParityNotes = (customer = {}, articles = []) => {
  const notes = [];
  const socialCount = SOCIAL_URL_KEYS.filter(
    (p) => customer?.social_links?.[p] && customer?.social_links?.[`${p}_enabled`]
  ).length;

  if ((customer.tile_style || 'soft') === 'gradient') {
    notes.push('Gradient tiles aren’t supported in the app — they appear solid.');
  }
  if ((customer.grid_cols || 3) !== 3) {
    notes.push(`The app always shows 3 tiles per row — your portal shows ${customer.grid_cols}.`);
  }
  if ((customer.tile_size || 'default') !== 'default') {
    notes.push('Tile size is fixed in the app — tiles are always square.');
  }
  if (customer.header_image_url && customer.show_header_text !== false) {
    notes.push('With a header image set, the app shows the image only and hides the header text.');
  }
  if ((customer.header_text_font || 'Inter') !== 'Inter' || (customer.font_family || 'Inter') !== 'Inter') {
    notes.push('Custom fonts aren’t available in the app — it uses the system font.');
  }
  if ((customer.header_text_align || 'center') !== 'center') {
    notes.push('The app always centres header text.');
  }
  if ((customer.header_text_size || 'xl') !== 'xl') {
    notes.push('Header text is a fixed size in the app.');
  }
  if (socialCount > 0 && (customer.social_links_position || 'top') !== 'bottom') {
    notes.push('The app always places social links at the bottom of the screen.');
  }
  if ((customer.news_article_count || 3) > NATIVE.maxNewsItems) {
    notes.push(`The app shows at most ${NATIVE.maxNewsItems} news items.`);
  }
  if (customer.tile_color && customer.tile_color !== '#FFFFFF' && articles.length > 0) {
    notes.push('News cards are always white in the app.');
  }
  return notes;
};

export default function NativePreview({ customer = {}, tiles = [], articles = [], events = [] }) {
  const headerBg = customer.header_background_color || customer.primary_color || '#2563EB';
  const headerFg = customer.header_text_color || contrastText(headerBg);
  const primary = customer.primary_color || '#2563EB';
  const newsItems = articles.slice(0, NATIVE.maxNewsItems);
  const socialPlatforms = SOCIAL_URL_KEYS.filter(
    (p) => customer?.social_links?.[p] && customer?.social_links?.[`${p}_enabled`]
  );

  return (
    <div
      data-testid="native-preview"
      style={{
        backgroundColor: customer.background_color || '#F8FAFC',
        // The app has no web fonts — it renders with the device system face.
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: headerBg,
          paddingTop: NATIVE.headerPadTop,
          paddingBottom: NATIVE.headerPadBottom,
          textAlign: 'center',
        }}
        data-testid="native-preview-header"
      >
        {customer.header_image_url ? (
          <img
            src={getImageUrl(customer.header_image_url)}
            alt=""
            style={{
              width: '100%',
              height: NATIVE.headerImageHeight,
              objectFit: 'contain',
              marginBottom: 8,
              display: 'block',
            }}
          />
        ) : customer.show_header_text !== false ? (
          <p style={{ fontSize: NATIVE.headerTitleSize, fontWeight: 700, color: headerFg, padding: '0 20px', margin: 0 }}>
            {customer.header_text || customer.name || 'Taptile'}
          </p>
        ) : null}
        {customer.show_welcome_message !== false && (
          <p style={{ fontSize: NATIVE.welcomeSize, color: headerFg, opacity: 0.9, marginTop: 5, padding: '0 20px' }}>
            Welcome, Sarah Jones
          </p>
        )}
      </div>

      {/* Tiles section title */}
      {customer.show_tiles_section_title !== false && customer.tiles_section_title && (
        <p
          style={{
            fontSize: NATIVE.sectionTitleSize,
            fontWeight: 600,
            color: customer.tiles_section_title_color || '#1E293B',
            margin: '20px 15px 10px',
          }}
        >
          {customer.tiles_section_title}
        </p>
      )}

      {/* Tiles — always 3 per row, square */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', padding: 10 }} data-testid="native-preview-tiles">
        {tiles.map((tile, i) => {
          const v = tileVisuals(tile, customer);
          const isLibrary = isLibraryIcon(tile.icon);
          const isImage = tile.icon && (tile.icon.startsWith('data:') || tile.icon.startsWith('http'));
          return (
            <div
              key={tile.id || i}
              style={{
                width: `${NATIVE.tileWidthPct}%`,
                aspectRatio: '1 / 1',
                margin: '2%',
                borderRadius: borderRadiusFor(customer.tile_shape),
                background: v.background,
                border: v.border || 'none',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 10,
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              {customer.show_tile_icon !== false && tile.icon && (
                <div style={{ marginBottom: 8, height: 32, display: 'flex', alignItems: 'center' }}>
                  {isImage ? (
                    <img src={tile.icon} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                  ) : isLibrary ? (
                    <LibraryIcon iconString={tile.icon} className="w-8 h-8" color={tile.icon_color || v.color} />
                  ) : (
                    <span style={{ fontSize: 28, lineHeight: 1 }}>{tile.icon}</span>
                  )}
                </div>
              )}
              {customer.show_tile_title !== false && (
                <span style={{ fontSize: NATIVE.tileTitleSize, fontWeight: 500, color: v.color, textAlign: 'center' }}>
                  {tile.title}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* News — one white card per article */}
      {customer.news_enabled !== false && newsItems.map((article, idx) => (
        <div
          key={article.id || idx}
          style={{ backgroundColor: '#fff', margin: 15, borderRadius: 12, padding: 15, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
          data-testid={`native-preview-news-${idx}`}
        >
          {idx === 0 && (
            <p style={{ fontSize: 12, color: '#666', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
              Latest News
            </p>
          )}
          {article.image_url && (
            <img
              src={getImageUrl(article.image_url)}
              alt=""
              style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 8, marginBottom: 10 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <p style={{ fontSize: NATIVE.newsTitleSize, fontWeight: 700, marginBottom: 8, color: '#1E293B' }}>{article.title}</p>
          <p style={{ fontSize: NATIVE.newsExcerptSize, color: '#64748B', lineHeight: '20px' }}>
            {stripHtml(article.excerpt || article.content || article.body || '').substring(0, 180)}
          </p>
          <div
            style={{
              marginTop: 12, display: 'inline-block', padding: '6px 14px', borderRadius: 20,
              border: `1.5px solid ${primary}`, color: primary, fontWeight: 600, fontSize: 13,
            }}
          >
            Read more →
          </div>
        </div>
      ))}

      {/* Events button */}
      {customer.nexus_events_enabled && (
        <div
          style={{
            display: 'flex', alignItems: 'center', margin: 15, padding: 16, backgroundColor: '#fff',
            borderRadius: 12, border: `1.5px solid ${primary}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
          data-testid="native-preview-events"
        >
          <Calendar className="w-5 h-5" style={{ color: primary }} />
          <div style={{ flex: 1, marginLeft: 12 }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: primary }}>Upcoming Events</p>
            <p style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
              {events.length || 3} events · Tap to view
            </p>
          </div>
          <ChevronRight className="w-4 h-4" style={{ color: primary }} />
        </div>
      )}

      {/* Social — always last in the app */}
      {customer.social_links_enabled !== false && socialPlatforms.length > 0 && (
        <div
          style={{
            backgroundColor: customer.social_background_color || '#FFFFFF',
            padding: 20, margin: '10px 15px', borderRadius: 12,
          }}
          data-testid="native-preview-social"
        >
          {customer.show_social_section_title !== false && (
            <p style={{ fontSize: 16, fontWeight: 600, textAlign: 'center', marginBottom: 15, color: customer.social_section_title_color || '#1E293B' }}>
              {customer.social_section_title || 'Connect With Us'}
            </p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
            {socialPlatforms.map((platform) => {
              const data = socialMediaIcons[platform];
              if (!data) return null;
              const { icon: Icon, color } = data;
              return (
                <div
                  key={platform}
                  style={{
                    width: NATIVE.socialIconSize, height: NATIVE.socialIconSize, borderRadius: '50%',
                    backgroundColor: color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '5px 8px',
                  }}
                >
                  <Icon className="w-5 h-5" style={{ color: '#FFFFFF' }} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
